import { useState, useEffect, useCallback, createContext, useContext } from "react";

const AuthContext = createContext(null);

const STORAGE_KEY = "wrvu_auth";

function getStoredAuth() {
  // Check localStorage first (remember me), then sessionStorage
  const stored = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
}

function setStoredAuth(data, rememberMe) {
  const storage = rememberMe ? localStorage : sessionStorage;
  // Clear both storages first
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
  // Set in the appropriate storage
  if (data) {
    storage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
}

function clearStoredAuth() {
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from storage
  useEffect(() => {
    const stored = getStoredAuth();
    if (stored && stored.token && stored.user) {
      setToken(stored.token);
      setUser(stored.user);
    }
    setLoading(false);
  }, []);

  const login = useCallback((authData, rememberMe = false) => {
    const { token: newToken, user: newUser } = authData;
    setToken(newToken);
    setUser(newUser);
    setStoredAuth({ token: newToken, user: newUser }, rememberMe);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    clearStoredAuth();
  }, []);

  const value = {
    user,
    token,
    isAuthenticated: !!token,
    isAdmin: user?.is_admin || false,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
